import { Box, Grid, Typography } from '@mui/material'
import { useStyles } from './style'
import { FaqGrid } from './FaqGrid'
import { VideoGrid } from './VideoGrid'
import ResourceCards from './ResourceCards'
import { NeedHelp } from './NeedHelp'
import { SupportPageNav } from './SupportPageNav'

export default function Help() {
  const classes = useStyles()

  return (
    <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box id="about" className={classes.helpContainer}>
        <Typography variant="h1">
          Support: Help and Resources
        </Typography>
        <Typography variant="body1">
        Find answers to common ETA Event Planner questions, key resources, how-to videos, and the ability to <a href="#contact-us">contact us</a>.
        </Typography>
      </Box>

      <Box className={classes.faqContainer}>
        <div id="faqs"></div>
        <Typography variant="h2">
          FAQs
        </Typography>
        <div id="faq-requestors"></div>
        <Typography variant="h3">
          For Requestors
        </Typography>
        <FaqGrid questions={[
          {
            question: 'What is the Event Planner used for?',
            answer: 'The Event Planner is a scheduling, approval, and coordination tool for technical and compliance assistance events hosted on WorkforceGPS through webinars and webcasts. It was created to streamline requesting events, ensure required details get captured, event approvals, and all logistics get coordinated effectively.'
          },
          {
            question: 'How far in advance should I submit a request for an event, and why might specific dates and times be unavailable?',
            answer: 'Submit your request as early as possible to ensure availability. For webinars, you must submit at least 10 business days before the live event. There is no maximum lead time; you can submit as far ahead as you’d like. If a date/time isn’t selectable, it is already booked and can’t be requested.'
          },
          {
            question: 'What information is required to submit a request for an event?',
            answer: 'Requestors are the Point of Contact (POC) responsible for program office event coordination. You’ll need to input a <b>federal moderator’s</b>; name, title, and contact details for presenters and support staff; an event <b>title</b> (max 100 characters), <b>summary</b> (max 500 characters), and <b>description</b> (max 2,500 characters); plus dates and times for the required <b>dry run</b> and <b>live event</b>.'
          },
          {
            question: 'How will I know when my event is approved?',
            answer: 'Once your event is approved, you will get an email from the Event Planner. You can also log in anytime to check the status of your event from the Event Planner homepage.'
          },
          {
            question: 'Can I request something that is not listed in the Event Planner?',
            answer: 'Yes. Use the “Additional Features & Requests” field on the “Features and Requests” page of the Event Planner to note additional needs. After your event is approved, Event Support will follow-up to confirm what can be accommodated.'
          },
          {
            question: 'My event details have changed. What do I do?',
            answer: 'Log in to the Event Planner and select the event you want to edit. Make your edits and re-submit your revised event for approval. After your event is re-approved, Event Support will follow-up to confirm what can be accommodated.'
          }
        ]} />
        <div id="faq-approvers"></div>
        <Typography variant="h3">
          For Approvers
        </Typography>
        <FaqGrid questions={[
          {
            question: 'What happens when I ask an event requestor to edit their event (“pass back”)?',
            answer: 'This unlocks the event for the requester and the requester is sent an email letting them know their event needs edits. Be sure to include notes explaining what needs to be changed or why the event wasn’t approved.'
            },
          {
            question: 'I’ll be unavailable to serve as my program office’s approver. What should I do?',
            answer: 'Designate someone from your program office as an alternate approver. Ask that person to create an Event Planner account if one doesn’t already exist. Contact us at <a href="mailto:events@workforcegps.org">events@workforcegps.org</a> so Event Support can assign them approver access.'
          },
          {
            question: 'I’m currently the only approver for my program office. Is that okay?',
            answer: 'It’s highly recommended that all program offices have two approvers (primary and alternate) for continuity of operations. Contact us at <a href="mailto:events@workforcegps.org">events@workforcegps.org</a> to designate an alternate approver.'
            },
          {
            question: 'How long do I have to approve an event, and what happens if I miss the window?',
            answer: 'You have two business days to approve an event before it gets released from the Event Planner. If the Event Planner releases the event request, the requester must re-submit it for approval.'
          }
        ]} />
      </Box>

      <Grid container>
        <Grid item xs={12} md={12}>
          <div id="resources"></div>
          <Typography variant="h2">
            Event Resources
          </Typography>
          <Typography variant="h3">
            WorkforceGPS TA Help - For ETA
          </Typography>
          <Typography variant="body1" sx={{ mb: 5 }}>
            To gain access to the Event Planner resources below and receive community notifications, join the private <a href="https://tahelp.workforcegps.org/" target="_blank" rel="noopener noreferrer">TA Help Community</a> on WorkforceGPS.  You have automatic access to the community if you have a WorkforceGPS account with a “.gov” email address.  If you are unable to access the community, contact <a href="mailto:events@workforcegps.org">events@workforcegps.org</a> for assistance.
          </Typography>

          {/* 6 cards */}
          <ResourceCards xs={12} md={4} links={[
              { title: "Webinars 101", text: "Resources designed to help you plan, execute, and optimize your technical assistance (TA) webinars. ", path: "https://tahelp.workforcegps.org/resources/2024/08/08/17/14/Webinars_101" },
              { title: "Zoom Meetings vs Zoom Webinars", text: "WorkforceGPS events can be scheduled in a Zoom Webinar room or a Zoom Meeting room. These two options provide different features that you should consider when planning your event.", path: "https://tahelp.workforcegps.org/resources/2024/11/12/14/37/Zoom_Meetings_vs_Zoom_Webinars_for_WorkforceGPS" },
              { title: "Recording Guides: Webcasts and Webcams", text: "Execute your plan – based on content and delivery strategy.", path: "https://tahelp.workforcegps.org/resources/2018/03/29/20/45/Recording-Guides-Webcast-Podcast-Webcams" },
              { title: "WorkforceGPS Event Preparation & Requirements", text: "By following these instructions, you'll contribute to the success of your event and enhance its overall impact.", path: "https://tahelp.workforcegps.org/resources/2023/10/27/19/03/WorkforceGPS_Webinar_Preparation_and_Requirements" },
              { title: "Webcast Preparation and Requirements", text: "This guide was created to ensure you have all the information you need so you understand what's required to prepare for your recording.", path: "https://tahelp.workforcegps.org/resources/2021/06/16/13/15/Preparing-for-a-Webcast-or-Podcast" },
              { title: "WorkforceGPS Templates", text: "Updated 9/11/24 The WorkforceGPS templates consist of three PowerPoint Templates and three Word templates.", path: "https://tahelp.workforcegps.org/resources/2018/03/30/17/06/WorkforceGPS-PowerPoint-Template" },
            ]}
          />
        </Grid>

      </Grid>

      <Box>
        <div id="how-to"></div>
        <Typography variant="h2">
          Quick How-To Videos
        </Typography>
        <Typography variant="h3">
          For Requestors and Approvers
        </Typography>
        <VideoGrid videoLinks={[
          {
            title: 'Overview',
            description: '',
            video: 'https://vimeo.com/867198341'
          },
          {
            title: 'How to Request an Account',
            description: '',
            video: 'https://vimeo.com/867199294'
          },
          {
            title: 'How to Reset a Password',
            description: '',
            video: 'https://vimeo.com/867199644'
          },
          {
            title: 'Edit or Cancel an Event',
            description: '',
            video: 'https://vimeo.com/867200061'
          },
          {
            title: 'How to Approve an Event',
            description: '',
            video: 'https://vimeo.com/867200557'
          },
          {
            title: 'Schedule a Webinar',
            description: '',
            video: 'https://vimeo.com/867253922'
          }
        ]} />
      </Box>

      <div id="contact-us" />
      <NeedHelp />
      </Box>
      <SupportPageNav />
    </Box>
  )
}
